import { useEffect, useRef, useState } from "react";

/** How long the flip acknowledgment stays visible, in ms. Mirrors the
 * `flash-success` keyframe duration in `tailwind.config.js` — keep them in sync. */
const FLIP_ACK_DURATION_MS = 900;

/**
 * True for a short window after `done` transitions false → true, then false again.
 *
 * Only fires on that transition, never on mount. A step that is already done when
 * the panel first renders hasn't just been accomplished — the user didn't do
 * anything, so celebrating it would just be noise on every page load.
 */
const useJustFlipped = (done: boolean): boolean => {
  const [justFlipped, setJustFlipped] = useState(false);
  const previous = useRef(done);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const flippedToDone = !previous.current && done;
    previous.current = done;

    if (!flippedToDone) return;

    setJustFlipped(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setJustFlipped(false),
      FLIP_ACK_DURATION_MS,
    );

    return () => clearTimeout(timer.current);
  }, [done]);

  return justFlipped;
};

export default useJustFlipped;
